import type { DayTradingStreamInfo } from '@/hooks/useDayTradingWebSocket';

/**
 * True if the stream has enough data to be opened/embedded for the current mode.
 * NONE is never configured.
 */
export function isStreamConfigured(streamInfo: DayTradingStreamInfo): boolean {
  switch (streamInfo.mode) {
    case 'YOUTUBE':     return streamInfo.youtubeURL !== '';
    case 'DIRECT':      return streamInfo.directStreamViewerURL !== '';
    case 'GOOGLE_MEET': return streamInfo.googleMeetURL !== '';
    case 'DISCORD':     return streamInfo.discordURL !== '';
    case 'NONE':        return false;
  }
}

/**
 * The user-facing URL to open the stream in a new tab. Empty string when not
 * configured or when the mode has no external URL (NONE).
 */
export function getStreamExternalURL(streamInfo: DayTradingStreamInfo): string {
  switch (streamInfo.mode) {
    case 'YOUTUBE':     return streamInfo.youtubeURL;
    case 'DIRECT':      return streamInfo.directStreamViewerURL;
    case 'GOOGLE_MEET': return streamInfo.googleMeetURL;
    case 'DISCORD':     return streamInfo.discordURL;
    case 'NONE':        return '';
  }
}

/**
 * The "source key" — a value that changes when the embedded source changes,
 * used both as a React `key` for PiP remounts and as the trigger for the
 * auto-restore-on-change effect in viewer pages. Empty when not embeddable.
 */
export function getStreamSourceKey(streamInfo: DayTradingStreamInfo): string {
  switch (streamInfo.mode) {
    case 'YOUTUBE': return streamInfo.youtubeURL;
    case 'DIRECT':  return streamInfo.directStreamViewerURL;
    // GOOGLE_MEET / DISCORD have no embedded PiP, so no source key to track
    case 'GOOGLE_MEET':
    case 'DISCORD':
    case 'NONE':
      return '';
  }
}

/** True if the mode renders a draggable PiP (i.e. YOUTUBE or DIRECT). */
export function hasStreamPiP(streamInfo: DayTradingStreamInfo): boolean {
  return streamInfo.mode === 'YOUTUBE' || streamInfo.mode === 'DIRECT';
}
